import React, { useEffect, useState } from 'react';
import axios from 'axios';
import api from '../lib/axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { SimpleSelect } from '../components/ui/simple-select';
import { HelpTooltip, TitleWithHelp } from '../components/ui/help-tooltip';
import { Edit, Trash2, ChevronDown, ChevronRight, Layers, CheckCircle2, Plus, Clock, Settings2, ExternalLink, GitBranch } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('nautilus_token')}` }
});

// Gera classes de cor a partir de um hex
const hexToColorClasses = (hex) => {
  if (!hex) hex = '#3B82F6';
  return {
    bg: `bg-opacity-10`,
    border: '',
    text: '',
    badge: '',
    hex: hex,
  };
};

const ProcessMapping = () => {
  const [processes, setProcesses] = useState([]);
  const [etapasMacro, setEtapasMacro] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedMacros, setExpandedMacros] = useState({});
  
  // Dialogs
  const [showFaseDialog, setShowFaseDialog] = useState(false);
  const [showMacroDialog, setShowMacroDialog] = useState(false);
  const [editingFase, setEditingFase] = useState(null);
  const [editingMacro, setEditingMacro] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  // Form data
  const [faseForm, setFaseForm] = useState({
    nome: '', descricao: '', duration_hours: 0, etapa_macro: 1,
    applies_to_all_vessels: true, excluded_vessel_types: '',
    applies_to_all_factories: true, excluded_factories: '',
    doc_url: '',
    etapas: []
  });
  const [macroForm, setMacroForm] = useState({ numero: 5, nome: '', cor: '#3B82F6' });
  const [deleteMacroId, setDeleteMacroId] = useState(null);

  useEffect(() => {
    document.title = "Mapeamento de Processos — NautilusOS";
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [processRes, macroRes] = await Promise.all([
        api.get('/process-templates'),
        api.get('/etapas-macro')
      ]);
      setProcesses(processRes.data);
      setEtapasMacro(macroRes.data);
      
      // Expandir todas as etapas macro por padrão
      const expanded = {};
      macroRes.data.forEach(m => { expanded[m.numero] = true; });
      setExpandedMacros(expanded);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  // Agrupar processos por etapa macro
  const groupByEtapaMacro = () => {
    const groups = {};
    etapasMacro.forEach(m => { groups[m.numero] = []; });
    processes.forEach(p => {
      const macro = p.etapa_macro || 1;
      if (groups[macro]) groups[macro].push(p);
    });
    return groups;
  };

  const toggleMacro = (num) => {
    setExpandedMacros(prev => ({ ...prev, [num]: !prev[num] }));
  };

  // ============ FASE HANDLERS ============
  const openNewFase = () => {
    setEditingFase(null);
    setFaseForm({
      nome: '', descricao: '', duration_hours: 0, etapa_macro: 1,
      applies_to_all_vessels: true, excluded_vessel_types: '',
      applies_to_all_factories: true, excluded_factories: '',
      doc_url: '',
      etapas: [{ name: '', duration_hours: 0, doc_url: '' }]
    });
    setShowFaseDialog(true);
  };

  const openEditFase = (fase) => {
    setEditingFase(fase);
    const cleanName = fase.name.includes('–') ? fase.name.split('–')[1].trim() : fase.name;
    setFaseForm({
      nome: cleanName,
      descricao: fase.description || '',
      duration_hours: fase.duration_hours || 0,
      etapa_macro: fase.etapa_macro || 1,
      applies_to_all_vessels: fase.applies_to_all_vessels !== false,
      excluded_vessel_types: (fase.excluded_vessel_types || []).join(', '),
      applies_to_all_factories: fase.applies_to_all_factories !== false,
      excluded_factories: (fase.excluded_factories || []).join(', '),
      doc_url: fase.doc_url || '',
      etapas: (fase.steps || []).map(s => ({ name: s.name, duration_hours: s.duration_hours || 0, doc_url: s.doc_url || '' }))
    });
    setShowFaseDialog(true);
  };

  const saveFase = async () => {
    try {
      const etapaMacro = etapasMacro.find(m => m.numero === faseForm.etapa_macro);
      
      // Calcular número da fase DENTRO da etapa macro selecionada
      let faseNum;
      if (editingFase) {
        faseNum = editingFase.name.match(/Fase (\d+)/)?.[1];
      } else {
        const fasesNaMacro = processes.filter(p => p.etapa_macro === faseForm.etapa_macro);
        faseNum = fasesNaMacro.length + 1;
      }
      
      const payload = {
        name: `Fase ${faseNum} – ${faseForm.nome}`,
        description: faseForm.descricao,
        duration_hours: Number(faseForm.duration_hours) || 0,
        etapa_macro: faseForm.etapa_macro,
        etapa_macro_nome: etapaMacro?.nome || 'Projeto e Planejamento',
        order: editingFase?.order ?? processes.filter(p => p.etapa_macro === faseForm.etapa_macro).length,
        applies_to_all_vessels: faseForm.applies_to_all_vessels,
        excluded_vessel_types: faseForm.excluded_vessel_types.split(',').map(s => s.trim()).filter(Boolean),
        applies_to_all_factories: faseForm.applies_to_all_factories,
        excluded_factories: faseForm.excluded_factories.split(',').map(s => s.trim()).filter(Boolean),
        doc_url: faseForm.doc_url || '',
        steps: faseForm.etapas.map(e => ({
          name: e.name,
          duration_hours: Number(e.duration_hours) || 0,
          description: '',
          doc_url: e.doc_url || ''
        }))
      };

      if (editingFase) {
        await api.put(`/process-templates/${editingFase.id}`, payload);
        toast.success('Fase atualizada!');
      } else {
        await api.post('/process-templates', payload);
        toast.success('Fase criada!');
      }
      
      setShowFaseDialog(false);
      loadData();
    } catch (error) {
      toast.error('Erro ao salvar fase');
    }
  };

  const deleteFase = async () => {
    try {
      await api.delete(`/process-templates/${deleteTarget.id}`);
      toast.success('Fase excluída!');
      setDeleteTarget(null);
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  // ============ MACRO HANDLERS ============
  const openNewMacro = () => {
    setEditingMacro(null);
    setMacroForm({ numero: etapasMacro.length + 1, nome: '', cor: '#3B82F6' });
    setShowMacroDialog(true);
  };

  const openEditMacro = (macro) => {
    setEditingMacro(macro);
    setMacroForm({ numero: macro.numero, nome: macro.nome, cor: macro.cor || '#3B82F6' });
    setShowMacroDialog(true);
  };

  const saveMacro = async () => {
    try {
      if (editingMacro) {
        await api.put(`/etapas-macro/${editingMacro.id}`, macroForm);
        toast.success('Etapa macro atualizada!');
      } else {
        await api.post('/etapas-macro', macroForm);
        toast.success('Etapa macro criada!');
      }
      setShowMacroDialog(false);
      loadData();
    } catch (error) {
      toast.error('Erro ao salvar');
    }
  };

  const deleteMacro = async () => {
    try {
      // Verificar se há fases associadas
      const fasesAssociadas = processes.filter(p => p.etapa_macro === deleteMacroId.numero);
      if (fasesAssociadas.length > 0) {
        toast.error(`Não é possível excluir. Existem ${fasesAssociadas.length} fase(s) associada(s) a esta etapa macro.`);
        setDeleteMacroId(null);
        return;
      }
      await api.delete(`/etapas-macro/${deleteMacroId.id}`);
      toast.success('Etapa macro excluída!');
      setDeleteMacroId(null);
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  // ============ ETAPA MICRO HANDLERS ============
  const addEtapaMicro = () => {
    setFaseForm({ ...faseForm, etapas: [...faseForm.etapas, { name: '', duration_hours: 0, doc_url: '' }] });
  };

  const removeEtapaMicro = (index) => {
    setFaseForm({ ...faseForm, etapas: faseForm.etapas.filter((_, i) => i !== index) });
  };

  const updateEtapaMicro = (index, field, value) => {
    const newEtapas = [...faseForm.etapas];
    newEtapas[index] = { ...newEtapas[index], [field]: value };
    setFaseForm({ ...faseForm, etapas: newEtapas });
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Carregando...</div>;
  }

  const groupedProcesses = groupByEtapaMacro();
  const totalMicros = processes.reduce((acc, p) => acc + (p.steps?.length || 0), 0);
  const totalHoras = processes.reduce((acc, p) => acc + (p.duration_hours || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <TitleWithHelp
            title="Mapeamento de Processos"
            description="Template master de produção naval. Define a hierarquia de etapas: Etapas Macro > Fases > Etapas Micro. Este template é usado como base para cronogramas de novos projetos."
            connections={['Modelos de Embarcação', 'Cronograma']}
          >
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <GitBranch className="h-6 w-6 text-blue-600" />
              Mapeamento de Processos
            </h1>
          </TitleWithHelp>
          <p className="text-gray-500 mt-1">Template padrão de produção naval</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={openNewMacro}>
            <Settings2 className="h-4 w-4 mr-2" />
            Nova Etapa Macro
          </Button>
          <Button onClick={openNewFase}>
            <Plus className="h-4 w-4 mr-2" />
            Nova Fase
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-6">
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-600">{etapasMacro.length}</div>
          <div className="text-xs text-gray-500 flex items-center gap-1 justify-center">
            Etapas Macro
            <HelpTooltip 
              title="Etapas Macro"
              description="Grandes grupos de produção (ex: Projeto, Laminação, Acabamento). Organizam as fases em categorias."
              iconSize={12}
            />
          </div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-purple-600">{processes.length}</div>
          <div className="text-xs text-gray-500 flex items-center gap-1 justify-center">
            Fases
            <HelpTooltip 
              title="Fases"
              description="Etapas de produção que compõem cada Etapa Macro. Cada fase pode conter múltiplas etapas micro."
              iconSize={12}
            />
          </div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">{totalMicros}</div>
          <div className="text-xs text-gray-500 flex items-center gap-1 justify-center">
            Etapas Micro
            <HelpTooltip 
              title="Etapas Micro"
              description="Tarefas detalhadas dentro de cada fase. São os itens de checklist que compõem o progresso da fase."
              iconSize={12}
            />
          </div>
        </div>
        {totalHoras > 0 && (
          <div className="text-center">
            <div className="text-2xl font-bold text-amber-600">{totalHoras}h</div>
            <div className="text-xs text-gray-500">Total Estimado</div>
          </div>
        )}
      </div>

      {/* Legenda Etapas Macro */}
      <div className="flex flex-wrap gap-2">
        {etapasMacro.map((macro) => {
          const cor = macro.cor || '#3B82F6';
          const fasesCount = (groupByEtapaMacro()[macro.numero] || []).length;
          return (
            <div key={macro.id} className="flex items-center gap-1">
              <Badge 
                className="px-3 py-1 cursor-pointer hover:opacity-80"
                style={{ backgroundColor: cor + '20', color: cor, borderColor: cor }}
                onClick={() => openEditMacro(macro)}
              >
                {macro.numero}ª Etapa - {macro.nome}
                <Edit className="h-3 w-3 ml-2" />
              </Badge>
              {fasesCount === 0 && (
                <Button 
                  size="sm" 
                  variant="ghost" 
                  className="h-6 w-6 p-0 text-red-500 hover:bg-red-50"
                  onClick={() => setDeleteMacroId(macro)}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          );
        })}
      </div>

      {/* Grupos por Etapa Macro */}
      <div className="space-y-4">
        {etapasMacro.sort((a, b) => a.numero - b.numero).map((macro) => {
          const phases = groupedProcesses[macro.numero] || [];
          const cor = macro.cor || '#3B82F6';
          const isExpanded = expandedMacros[macro.numero];
          
          return (
            <Card key={macro.id} style={{ backgroundColor: cor + '0D', borderLeftColor: cor, borderLeftWidth: '4px' }}>
              <CardHeader className="cursor-pointer py-4" onClick={() => toggleMacro(macro.numero)}>
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    {isExpanded ? <ChevronDown className="h-5 w-5" style={{ color: cor }} /> : <ChevronRight className="h-5 w-5" style={{ color: cor }} />}
                    <Layers className="h-5 w-5" style={{ color: cor }} />
                    <CardTitle style={{ color: cor }}>
                      {macro.numero}ª ETAPA - {macro.nome.toUpperCase()}
                    </CardTitle>
                  </div>
                  <Badge style={{ backgroundColor: cor + '20', color: cor }}>{phases.length} fase(s)</Badge>
                </div>
              </CardHeader>
              
              {isExpanded && (
                <CardContent className="pt-0">
                  {phases.length === 0 ? (
                    <p className="text-gray-500 text-sm py-4 text-center">Nenhuma fase nesta etapa</p>
                  ) : (
                    <div className="space-y-3">
                      {phases.sort((a, b) => (a.order || 0) - (b.order || 0)).map((fase) => (
                        <Card key={fase.id} className="bg-white">
                          <CardContent className="py-4">
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2 flex-wrap">
                                  <span className="font-semibold text-lg">{fase.name}</span>
                                  <Badge variant="outline" className="text-xs">
                                    {fase.steps?.length || 0} etapas
                                  </Badge>
                                  {fase.duration_hours > 0 && (
                                    <Badge variant="outline" className="text-xs bg-amber-50">
                                      <Clock className="h-3 w-3 mr-1" />
                                      {fase.duration_hours}h
                                    </Badge>
                                  )}
                                  {fase.doc_url && (
                                    <a href={fase.doc_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                                      <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 cursor-pointer hover:bg-blue-100">
                                        <ExternalLink className="h-3 w-3 mr-1" />
                                        Documentação
                                      </Badge>
                                    </a>
                                  )}
                                  {!fase.applies_to_all_vessels && (
                                    <Badge variant="outline" className="text-xs bg-red-50 text-red-700">
                                      Restrição: Embarcações
                                    </Badge>
                                  )}
                                  {!fase.applies_to_all_factories && (
                                    <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700">
                                      Restrição: Fábricas
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600 mb-3">{fase.description}</p>
                                
                                {/* Etapas Micro */}
                                <Accordion type="single" collapsible>
                                  <AccordionItem value="steps" className="border-none">
                                    <AccordionTrigger className="py-2 text-sm hover:no-underline">
                                      <span className="flex items-center gap-2 text-blue-600">
                                        <CheckCircle2 className="h-4 w-4" />
                                        Ver etapas micro
                                      </span>
                                    </AccordionTrigger>
                                    <AccordionContent>
                                      <div className="grid gap-2 mt-2">
                                        {(fase.steps || []).map((step, idx) => (
                                          <div key={idx} className="flex items-center gap-2 text-sm py-1 px-3 bg-gray-50 rounded">
                                            <span className="text-gray-400 font-mono text-xs w-6">{idx + 1}.</span>
                                            <span className="flex-1">{step.name}</span>
                                            {step.duration_hours > 0 && (
                                              <Badge variant="outline" className="text-xs">{step.duration_hours}h</Badge>
                                            )}
                                            {step.doc_url && (
                                              <a href={step.doc_url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                                                <ExternalLink className="h-3 w-3 text-blue-500 hover:text-blue-700" />
                                              </a>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    </AccordionContent>
                                  </AccordionItem>
                                </Accordion>
                              </div>
                              
                              <div className="flex gap-1 ml-4">
                                <Button size="sm" variant="ghost" onClick={() => openEditFase(fase)}>
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setDeleteTarget({ type: 'fase', ...fase })}>
                                  <Trash2 className="h-4 w-4 text-red-500" />
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      {/* Dialog Fase */}
      <Dialog open={showFaseDialog} onOpenChange={setShowFaseDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingFase ? 'Editar Fase' : 'Nova Fase'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nome da Fase</Label>
                <Input value={faseForm.nome} onChange={(e) => setFaseForm({ ...faseForm, nome: e.target.value })} />
              </div>
              <div>
                <Label>Etapa Macro</Label>
                <SimpleSelect
                  options={etapasMacro.map(m => ({ value: m.numero, label: `${m.numero}ª - ${m.nome}` }))}
                  value={faseForm.etapa_macro}
                  onChange={(v) => setFaseForm({ ...faseForm, etapa_macro: Number(v) })}
                />
              </div>
            </div>

            <div>
              <Label>Descrição</Label>
              <Textarea value={faseForm.descricao} onChange={(e) => setFaseForm({ ...faseForm, descricao: e.target.value })} rows={2} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Duração Estimada da Fase (horas)</Label>
                <Input type="number" min="0" step="0.5" value={faseForm.duration_hours} onChange={(e) => setFaseForm({ ...faseForm, duration_hours: e.target.value })} />
              </div>
              <div>
                <Label>Link de Documentação (Google Drive/Notion)</Label>
                <Input type="url" value={faseForm.doc_url} onChange={(e) => setFaseForm({ ...faseForm, doc_url: e.target.value })} placeholder="https://..." />
              </div>
            </div>

            {/* Restrições */}
            <div className="grid grid-cols-2 gap-4">
              <div className="border p-3 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <input type="checkbox" checked={faseForm.applies_to_all_vessels} onChange={(e) => setFaseForm({ ...faseForm, applies_to_all_vessels: e.target.checked })} />
                  <Label className="text-sm">Aplica a todos os tipos de embarcação</Label>
                </div>
                {faseForm.applies_to_all_vessels && (
                  <div>
                    <Label className="text-xs text-gray-500">Exceto (separado por vírgula):</Label>
                    <Input value={faseForm.excluded_vessel_types} onChange={(e) => setFaseForm({ ...faseForm, excluded_vessel_types: e.target.value })} placeholder="Ex: Jet ski, Veleiro" />
                  </div>
                )}
              </div>
              
              <div className="border p-3 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <input type="checkbox" checked={faseForm.applies_to_all_factories} onChange={(e) => setFaseForm({ ...faseForm, applies_to_all_factories: e.target.checked })} />
                  <Label className="text-sm">Pode ser feita em qualquer fábrica</Label>
                </div>
                {faseForm.applies_to_all_factories && (
                  <div>
                    <Label className="text-xs text-gray-500">Exceto (separado por vírgula):</Label>
                    <Input value={faseForm.excluded_factories} onChange={(e) => setFaseForm({ ...faseForm, excluded_factories: e.target.value })} placeholder="Ex: Fábrica A, Fábrica B" />
                  </div>
                )}
              </div>
            </div>

            {/* Etapas Micro */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <Label>Etapas Micro</Label>
                <Button type="button" size="sm" onClick={addEtapaMicro}>+ Adicionar</Button>
              </div>
              <div className="space-y-2 max-h-[250px] overflow-y-auto">
                {faseForm.etapas.map((etapa, idx) => (
                  <div key={idx} className="flex gap-2 items-center p-2 bg-gray-50 rounded">
                    <span className="text-gray-400 text-sm w-6">{idx + 1}.</span>
                    <Input className="flex-1" value={etapa.name} onChange={(e) => updateEtapaMicro(idx, 'name', e.target.value)} placeholder="Nome da etapa" />
                    <Input className="w-20" type="number" min="0" step="0.5" value={etapa.duration_hours} onChange={(e) => updateEtapaMicro(idx, 'duration_hours', e.target.value)} placeholder="Horas" title="Horas estimadas" />
                    <Input className="w-40" type="url" value={etapa.doc_url || ''} onChange={(e) => updateEtapaMicro(idx, 'doc_url', e.target.value)} placeholder="Link doc..." title="Link de documentação" />
                    <Button size="sm" variant="ghost" onClick={() => removeEtapaMicro(idx)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFaseDialog(false)}>Cancelar</Button>
            <Button onClick={saveFase}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Etapa Macro */}
      <Dialog open={showMacroDialog} onOpenChange={setShowMacroDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingMacro ? 'Editar Etapa Macro' : 'Nova Etapa Macro'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label>Número</Label>
              <Input type="number" min="1" value={macroForm.numero} onChange={(e) => setMacroForm({ ...macroForm, numero: Number(e.target.value) })} />
            </div>
            <div>
              <Label>Nome</Label>
              <Input value={macroForm.nome} onChange={(e) => setMacroForm({ ...macroForm, nome: e.target.value })} placeholder="Ex: Projeto e Planejamento" />
            </div>
            <div>
              <Label>Cor</Label>
              <div className="flex gap-2">
                <Input type="color" value={macroForm.cor} onChange={(e) => setMacroForm({ ...macroForm, cor: e.target.value })} className="w-16 h-10" />
                <Input value={macroForm.cor} onChange={(e) => setMacroForm({ ...macroForm, cor: e.target.value })} placeholder="#3B82F6" />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMacroDialog(false)}>Cancelar</Button>
            <Button onClick={saveMacro}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Confirmação de Exclusão de Fase */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir &quot;{deleteTarget?.name || deleteTarget?.nome}&quot;?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={deleteFase} className="bg-red-600 hover:bg-red-700">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog Confirmação de Exclusão de Etapa Macro */}
      <AlertDialog open={!!deleteMacroId} onOpenChange={() => setDeleteMacroId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Etapa Macro</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir a etapa macro &quot;{deleteMacroId?.numero}ª - {deleteMacroId?.nome}&quot;?
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={deleteMacro} className="bg-red-600 hover:bg-red-700">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProcessMapping;
